<template>
  <div v-loading.fullscreen="loading" class="flex flex-col responsive-page fixed-table">
    <div class="items-center pb-8  header-container">
      <div class="flex mx--2 barcode-search">
        <div class="flex px-2px header-container__left">
          <h4 class="font-bold text-lg truncate leading-normal items-center flex mr-4">
            {{ $t('barcode.search') }}
          </h4>
          <div class="flex items-center gap-7">
            <span>
              {{ projectName }}
            </span>
          </div>

          <el-button class="ml-4" type="default" size="medium" @click="openSavedSearchDialog">
            {{ matchedSavedCondition ? matchedSavedCondition.name : $t('saved_search_conditions') }}
          </el-button>
        </div>
        <div class="items-center flex-grow px-1px flex justify-end header-container__right">
          <div v-if="latestImportHistory" class="flex items-center">
            <h4 class="font-bold text-lg truncate leading-normal items-center flex mr-4">
              {{ $t('import_history.latest_plan_import_time') }}
            </h4>
            <span class="font-bold text-base truncate items-center flex border-solid p-1 px-2">
              {{ latestImportTime }}
            </span>
            <a
              class="flex items-center btn-refresh"
              :class="{ 'animate-spin pointer-events-none': isLoadingImportHistory }"
              @click="loadLatestImportHistory()"
            >
              <IconRefresh />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      class="search-container relative rounded-md mb-4 mx-3 pt-2 pl-2 pb-4 border-solid border border-gray-300 whitespace-nowrap align-middle"
    >
      <el-tooltip
        v-if="!!dataSearch?.searchConditionBlocks"
        :content="
          matchedSavedCondition
            ? matchedSavedCondition.name || $t('delete_saved_search_condition')
            : $t('save_search_condition')
        "
        placement="top"
      >
        <span class="absolute right-3 save-condition-icon flex items-center justify-center cursor-pointer z-10">
          <IconStar
            class="w-full h-full transition-transform duration-150 hover:scale-125"
            :fill="matchedSavedCondition ? 'currentColor' : 'none'"
            :class="matchedSavedCondition ? 'text-yellow-400' : 'hover:text-yellow-400'"
            @click.stop="toggleSave"
          />
        </span>
      </el-tooltip>
      <el-form v-if="isSetPresetSearch" :disabled="isSetPresetSearch">
        <SearchForm
          ref="presetForm"
          :version="presetData.version"
          v-model:searchConditionBlocks="presetData.searchConditionBlocks"
          v-model:logicalOperator="presetData.logicalOperator"
        />
      </el-form>
      <SearchForm
        v-if="!!dataSearch?.searchConditionBlocks"
        ref="searchForm"
        :key="searchFormKey"
        :version="dataSearch.version"
        v-model:searchConditionBlocks="dataSearch.searchConditionBlocks"
        v-model:logicalOperator="dataSearch.logicalOperator"
      />
    </div>
    <div class="flex justify-between items-center ml-2 my-3">
      <div>
        <el-checkbox v-model="dataSearch.isToday">
          {{ $t('filtered_by_today') }}
        </el-checkbox>
      </div>

      <div class="flex justify-end mr-2">
        <el-button
          class="rounded-md hover:bg-white hover:text-black el-process-btn"
          type="default"
          style="padding-top: 9px; padding-bottom: 9px;"
          size="medium"
          @click="clearAllSearchForm()"
        >
          {{ $t('clearAll') }}
        </el-button>

        <el-popover
          v-if="isShowReportOutput"
          placement="bottom-end"
          :width="200"
          trigger="hover"
          popper-class="popover-barcode-list"
        >
          <template #reference>
            <el-button type="primary" size="medium" class="text-white focus:text-white" :disabled="loading">
              {{ $t('barcode.export_bulk_report_output') }}
            </el-button>
          </template>
          <div
            v-for="reportTemplate in barcodeReportTemplates"
            :key="reportTemplate.code"
            role="option"
            :class="['cursor-pointer text-center', 'text-black', 'my-2']"
            @click="exportReportOutputMO(reportTemplate.code || '')"
          >
            {{ reportTemplate.buttonLabel }}
          </div>
        </el-popover>

        <el-button type="default" class="rounded-md button-blue text-white" size="medium" @click="performSearch()">
          <div class="flex items-center">
            <span>{{ $t('search') }}</span>
            <IconSearch style="height:14px;" />
          </div>
        </el-button>
      </div>
    </div>
    <div class="flex justify-between pb-3 pt-2 header-bar items-center rounded-t-xl mt-3">
      <div class="ml-4 content-end font-bold text-base">
        {{ $t('total_barcode') }}: {{ totalBarcodes.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}
      </div>

      <div class="mr-4">
        <el-button
          v-if="!isLoggedAsClient"
          class="rounded-l-md pdf-download-btn"
          type="default"
          size="medium"
          :disabled="totalBarcodes <= 0"
          @click="downloadPDFBarcodes()"
        >
          <div class="flex flex-row items-center">
            <div class="mr-2" :class="{ 'opacity-50': totalBarcodes <= 0 }">
              <IconDownload />
            </div>
            <span :class="{ 'opacity-50': totalBarcodes <= 0 }">
              {{ $t('barcode.pdf_download') }}
            </span>
          </div>
        </el-button>
      </div>
    </div>

    <div v-if="barcodeList.length > 0" class="flex-grow flex flex-col">
      <div class="mb-4 flex-grow table-container">
        <el-table
          v-if="isShowTable"
          :data="barcodeList"
          :default-sort="defaultSort"
          class="table-content w-full"
          header-cell-class-name="custom-table__header-cell"
          :cell-class-name="getCellClassName"
          border
          :empty-text="$t('empty')"
          highlight-current-row
          @sort-change="sortBarcode"
          @row-click="onEditBarcode"
        >
          <el-table-column
            v-for="col in tableColumns"
            :key="col.prop"
            :prop="col.prop"
            :label="col.label"
            :min-width="col.minWidth"
            :width="col.width"
            :sortable="col.sortable"
          >
            <template v-slot="scope">
              <div v-if="col.custom" class="flex" v-show="col.condition(scope.row)">
                <div class="flex-grow flex items-center ml-4">{{ col.custom(scope.row) }}</div>
              </div>
              <span v-else-if="col.formatter">{{ col.formatter(scope.row) }}</span>
              <span v-else>{{ scope.row[col.prop] }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="justify-center flex flex-row mb-16 paging-container">
        <el-pagination
          layout="prev, pager, next"
          :total="totalBarcodes"
          :page-size="dataSearch.count"
          :current-page="currentPageNum"
          @current-change="currentPageChange($event)"
          background
          :class="totalBarcodes > 60 ? 'custom-pagination-no-last-page' : 'custom-pagination'"
        />
      </div>
      <BarcodePDF ref="barcodePDFComponent" :barcodes="exportBarcodes" :project="getCurrentProject" />
    </div>
    <template v-else>
      <ItemNotFound class="mt-56" :content="$t('empty')" />
    </template>
    <div class="dialog-area">
      <el-dialog
        custom-class="el-dialog--custom"
        v-model="isShowArchiveBCDialog"
        width="550px"
        top="0"
        :title="$t(!dataSearch.isToday ? 'archive_barcode' : 'unarchive_barcode')"
        :close-on-click-modal="false"
        :destroy-on-close="true"
        :show-close="true"
        @close="isShowArchiveBCDialog = false"
      >
        <div class="my-4 pre-line leading-8 text-center">
          {{
            $t(!dataSearch.isToday ? 'confirm_archive_barcode_message' : 'confirm_unarchive_barcode_message', {
              total_barcode: totalBarcodes,
            })
          }}
        </div>
        <template #footer>
          <div class="flex">
            <div class="flex-1">
              <el-button type="default" class="btn-default-cancel" @click="isShowArchiveBCDialog = false">
                {{ $t('barcode_type.cancel') }}
              </el-button>
            </div>
            <div class="flex-1">
              <el-button type="primary" @click="archiveOrUnarchive">
                {{ $t('confirm') }}
              </el-button>
            </div>
          </div>
        </template>
      </el-dialog>
      <SaveConditionDialog
        v-model="isSaveDialogVisible"
        :loading="isSavingCondition"
        :existing-names="savedSearchConditions.map((c) => c.name)"
        @confirm="saveSearchCondition"
      />
      <SavedSearchConditionDialog
        v-model="isSavedListVisible"
        :saved-search-conditions="savedSearchConditions"
        :matched-condition-id="matchedSavedCondition ? matchedSavedCondition.id : null"
        @apply="applySavedSearch"
      />
      <ImportHistoryDialog v-model="isImportHistoryVisible" :history="latestImportHistory" />
    </div>
  </div>
</template>

<script lang="ts">
import type { TableColumnCtx } from 'element-plus/lib/el-table/src/table-column/defaults'
import BarcodeIcon from '@/components/BarcodeIcon.vue'
import BarcodePDF from '@/components/barcodeImport/BarcodePDF.vue'
import ImportHistoryDialog from '@/components/barcodeSearch/ImportHistoryDialog.vue'
import SavedSearchConditionDialog from '@/components/barcodeSearch/SavedSearchConditionDialog.vue'
import SearchForm from '@/components/barcodeSearch/SearchForm.vue'
import ItemNotFound from '@/components/common/ItemNotFound.vue'
import SaveConditionDialog from '@/components/common/SaveConditionDialog.vue'
import DataTableMixin from '@/components/mixins/DataTableMixin.vue'
import IconArchive from '@/components/svg/IconArchive.vue'
import IconDownload from '@/components/svg/IconDownload.vue'
import IconPreset from '@/components/svg/IconPreset.vue'
import IconRefresh from '@/components/svg/IconRefresh.vue'
import IconSearch from '@/components/svg/IconSearch.vue'
import IconStar from '@/components/svg/IconStar.vue'
import IconVerticalThreeDots from '@/components/svg/IconVerticalThreeDots.vue'
import {
  CREATE_SAVED_SEARCH_CONDITION,
  DELETE_SAVED_SEARCH_CONDITION,
  EXPORT_BULK_REPORT_OUTPUT,
  LOAD_BARCODE_LIST,
  LOAD_PROJECTS_LIST,
  SET_HAVE_NEW_EXPORT_RECORD,
} from '@/store/actions'
import { getLatestImportHistory, IImportHistory } from '@/apis/masterDataAPI'
import { archiveBarcodes, exportBarCode, exportBarCodeAttchFiles, getBarcodeList } from '@/utils/api'
import {
  BARCODE_SEARCH_KEYS,
  BARCODE_SEARCH_VALUE_TYPES,
  EXPORT_BARCODES_LIMIT_NUMBER,
  TABLE_SORT_ASC,
  TABLE_SORT_DESC,
  TABLE_SORT_TYPE,
  TIMETABLE_DATETIME,
} from '@/utils/constants'
import { EExportType, ESmartBarcodeMenu, EUserType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import {
  buildSearchUrlQuery,
  deserializeConditionBlocks,
  formatQuantityWithUnit,
  formatSearchConditionBlocks,
  initDataSearch,
  isEmpty,
  objectToArray,
} from '@/utils/helpers'
import { formatDate } from '@/utils/timeUtils'
import {
  IBarcodeDefinitionType,
  IBarcodeSearch,
  IBarcodeSearchForm,
  IBarcodeSearchFormItem,
  IProject,
  ISavedSearchCondition,
  ISearchConditionBlock,
  ITableSortData,
  ITrackpoint,
  ITrackPointKeyVal,
} from '@/utils/types'
import { IManufacturingPlanSearch } from '@/utils/types/model/manufacturingPlan'
import { openMessage, sortTrackingPoints } from '@/utils/utils'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { Options } from 'vue-class-component'
import { Mixins, ProvideReactive, Watch } from 'vue-property-decorator'
import orderBy from 'lodash/orderBy'
type TListType = 'searchList' | 'presetList'

interface ITableColumn {
  prop: string
  label: string
  width?: number
  minWidth?: number
  sortable?: string | boolean
  formatter?: (row: IManufacturingPlanSearch) => string
  custom?: (row: IManufacturingPlanSearch) => string
  condition?: (row: IManufacturingPlanSearch) => boolean
}

@Options({
  components: {
    ItemNotFound,
    SearchForm,
    BarcodeIcon,
    IconDownload,
    BarcodePDF,
    IconSearch,
    IconPreset,
    IconRefresh,
    IconStar,
    IconVerticalThreeDots,
    IconArchive,
    SaveConditionDialog,
    SavedSearchConditionDialog,
    ImportHistoryDialog,
  },
  name: ESmartBarcodeMenu.BARCODE_SEARCH,
})
export default class Barcode extends Mixins(DataTableMixin) {
  @ProvideReactive() projectVersion = 0
  @ProvideReactive() trackingPointDatasArr: ITrackPointKeyVal[] = []

  isShowArchiveBCDialog = false

  isSaveDialogVisible = false
  isSavedListVisible = false
  isSavingCondition = false

  latestImportHistory: IImportHistory | null = null
  isLoadingImportHistory = false
  isImportHistoryVisible = false

  currentPageNum = 1
  dataSearch = {} as IBarcodeSearchForm
  searchFormKey = 0
  loading = false
  isShowTable = true
  exportBarcodes: IManufacturingPlanSearch[] = []

  get tableColumns(): ITableColumn[] {
    return [
      { prop: 'orderNumber', label: this.$t('barcode.orderNumber'), width: 100, sortable: true },
      {
        prop: 'manufactureCompletionDate',
        label: this.$t('manufacturing_date'),
        formatter: (row) => formatDate(row.manufactureCompletionDate, TIMETABLE_DATETIME.DATE_RESPONSE_FORMAT),
        sortable: true,
      },
      {
        prop: 'workCenterCode',
        label: this.$t('work_center_code'),
        sortable: true,
      },
      {
        prop: 'workCenterName',
        label: this.$t('work_center_name'),
      },
      {
        prop: 'manufacturedItemCode',
        label: this.$t('barcode.manufactureItemCode'),
        sortable: true,
      },
      {
        prop: 'manufacturedItemName',
        label: this.$t('barcode.manufactureItemName'),
        minWidth: 140,
      },
      {
        prop: 'plannedQuantityStockUnit',
        label: this.$t('barcode.plannedQuantityStockUnit'),
        formatter: (row) => formatQuantityWithUnit(Math.ceil(row.plannedQuantityStockUnit), row.stockUnit),
        sortable: true,
      },
      {
        prop: 'actualQuantity',
        label: this.$t('actual_quantity'),
        formatter: (row) => formatQuantityWithUnit(Math.ceil(row.actualQuantity ?? 0), row.stockUnit),
      },
      {
        prop: 'storageQuantity',
        label: this.$t('barcode.storage_quantity'),
        formatter: (row) => `${Math.ceil(Number(row.actualStorageQuantity ?? 0))} (${row.storageUnit})`,
      },
      {
        prop: 'usageRecordRegistration',
        formatter: (row) => this.formatUsageRecordRegistration(row.isUsageHistory),
        label: this.$t('barcode.usage_record_registration'),
      },
    ]
  }

  get latestImportTime(): string {
    const time = this.latestImportHistory?.updatedDateTime ?? this.latestImportHistory?.createdDateTime
    return time ? formatDate(time, TIMETABLE_DATETIME.DATETIME_WITH_SECOND_FORMAT) : ''
  }

  async loadLatestImportHistory() {
    this.isLoadingImportHistory = true
    try {
      this.latestImportHistory = await getLatestImportHistory()
    } catch (e) {
      this.latestImportHistory = null
    } finally {
      this.isLoadingImportHistory = false
    }
  }

  get projectName(): string {
    return this.getProjectsList?.[0]?.name ?? ''
  }

  formatUsageRecordRegistration(value?: boolean) {
    if (value) {
      return 'Y'
    } else {
      return 'N'
    }
  }

  getCellClassName() {
    return 'custom-table__cell cursor-pointer'
  }

  openArchiveDialog() {
    this.isShowArchiveBCDialog = true
  }

  get project() {
    return this.$store.state.barcode.projectList
  }

  get barcodeReportTemplates() {
    const barcodeTypes = this.project?.barcodeTypes
    return barcodeTypes ? (Object.values(barcodeTypes)[0] as IBarcodeDefinitionType)?.barcodeReportTemplates ?? [] : []
  }

  @Watch('dataSearch.version')
  updateProjectVersion() {
    this.projectVersion = this.dataSearch.version
  }

  get isSetPresetSearch() {
    return this.$store.state.profile?.user?.setPresetSearch
  }

  get isShowReportOutput() {
    return this.$store.getters.isShowReportOutput
  }

  get isShowExternalId() {
    return this.$store.getters.isShowExternalId
  }

  presetData = initDataSearch() as IBarcodeSearchForm
  async loadClientPreset() {
    if (!this.isLoggedAsClient || !this.isSetPresetSearch) return false
    const clientPreset = cloneDeep(this.$store.state.profile?.user?.condition)
    clientPreset.searchConditionBlocks.forEach(async (block: ISearchConditionBlock) => {
      // specially handle projectId
      if (block.searchConditions[0].key === 'projectId') block.searchConditions.splice(0, 1)

      block.searchConditions.forEach((condition) => {
        // specially handle subKey
        const keys = (condition.key ?? '').split('.')
        if (keys.length === 3) {
          condition.key = `${keys[0]}.${keys[1]}`
          condition.subKey = keys[2]
        }
      })
    })

    this.presetData = {
      ...this.dataSearch,
      ...clientPreset,
      projectCode: clientPreset.projectCode,
      version: clientPreset.version,
      count: 10,
    }
    this.dataSearch = {
      ...initDataSearch(),
      projectCode: clientPreset.projectCode,
      version: clientPreset.version,
    }

    return true
  }

  get totalBarcodes(): number {
    return this.$store.state.barcode?.totalBarcodes
  }

  get defaultSort() {
    return {
      prop: this.dataSearch?.sortKey || null,
      order:
        this.dataSearch?.sortOrder === 1 ? TABLE_SORT_ASC : this.dataSearch?.sortOrder === -1 ? TABLE_SORT_DESC : '',
    }
  }

  get loggedUser() {
    return this.$store.state.profile?.user || {}
  }

  get isLoggedAsClient() {
    return this.loggedUser.userType === EUserType.CLIENT || false
  }

  get isCSVButtonDisable(): boolean {
    const isSearchValue = this.dataSearch?.searchConditionBlocks?.some((block) => {
      return block.searchConditions?.some(
        (val) => !isEmpty(val.value) || (!isEmpty(val.minValue) && !isEmpty(val.maxValue))
      )
    })
    return !isSearchValue || this.barcodeList.length < 1
  }

  get isCSVCustomHeaderButtonDisable(): boolean {
    const isSearchValue = this.dataSearch?.searchConditionBlocks?.some((block) => {
      return block.searchConditions?.some(
        (val) => !isEmpty(val.value) || (!isEmpty(val.minValue) && !isEmpty(val.maxValue))
      )
    })
    const isValidBarcodeType = this.checkValidBarcodeType()

    return !isSearchValue || !this.dataSearch?.version || !isValidBarcodeType || this.barcodeList.length < 1
  }

  get barcodeList(): IManufacturingPlanSearch[] {
    return this.$store.state.barcode?.barcodes
  }

  get getCurrentProject(): IProject {
    return this.$store.state.barcode?.projectList
  }

  get getProjectsList(): IProject[] {
    return this.$store.state.project?.originalProjects.filter(
      (item: IProject) => !(item.isDraft && item.isDraft === true)
    )
  }

  checkValidBarcodeType() {
    let isValidBarcodeType = false
    if (this.dataSearch?.searchConditionBlocks) {
      for (const block of this.dataSearch.searchConditionBlocks) {
        for (const condition of block.searchConditions) {
          if (condition.key === 'barcodeType') {
            if (isValidBarcodeType) {
              return false
            }
            isValidBarcodeType = true
          }
        }
      }
    }
    return isValidBarcodeType
  }

  formatBarcodeType(barcodeTypeDefinition: IBarcodeDefinitionType): IBarcodeDefinitionType {
    return barcodeTypeDefinition as IBarcodeDefinitionType
  }

  async archiveOrUnarchive() {
    try {
      this.loading = true
      const condition = (() => {
        const condBlocks = [] as ISearchConditionBlock[]
        const searchData = cloneDeep(this.dataSearch)
        searchData.searchConditionBlocks.forEach((i) => condBlocks.push(cloneDeep(i)))

        return { ...searchData, searchConditionBlocks: formatSearchConditionBlocks(condBlocks) } as IBarcodeSearchForm
      })()

      await archiveBarcodes({
        archiveType: !this.dataSearch.isToday ? 'archive' : 'unarchive',
        condition,
      })

      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.ARCHIVE, isHaveNew: true })
      openMessage(this.$t('archive_barcode_success'), 'success')
    } catch (e) {
      errorHandler(e)
    } finally {
      this.isShowArchiveBCDialog = false
      this.loading = false
    }
  }

  listType: TListType = 'searchList'
  async fetchBarcodesList(data: IBarcodeSearchForm, type: TListType) {
    this.loading = true
    if (type === 'presetList') data = { ...this.presetData, skip: data.skip }

    this.listType = type
    this.isShowTable = false
    this.redirectIndex(data, this.currentPageNum)
    await this.$store.dispatch(LOAD_BARCODE_LIST, data).finally(() => (this.loading = false))
    setTimeout(() => (this.isShowTable = true))

    const newTPs = this.getCurrentProject.trackPoints
    if (isEmpty(newTPs)) return

    const arrTrackPoints = objectToArray<ITrackpoint>(cloneDeep(newTPs)) || []
    sortTrackingPoints(arrTrackPoints)
    this.trackingPointDatasArr = arrTrackPoints
  }

  async sortBarcode(data: ITableSortData) {
    if (data.order === null) {
      this.dataSearch.sortKey = ''
      this.dataSearch.sortOrder = 0
    } else {
      this.dataSearch.sortKey = data.prop ?? 'orderNumber'
      this.dataSearch.sortOrder = TABLE_SORT_TYPE[data.order ?? TABLE_SORT_DESC]
    }
    await this.fetchBarcodesList(this.dataSearch, this.listType)
  }

  async currentPageChange(pageNum: number) {
    if (this.isTriggerPageChanging) {
      this.currentPageNum = pageNum
      const skipNum = (Number(pageNum) - 1) * Number(this.dataSearch.count)
      this.dataSearch.skip = skipNum
      await this.fetchBarcodesList(this.dataSearch, this.listType)
    } else {
      this.isTriggerPageChanging = true
    }
  }

  async exportReportOutputMO(code: string) {
    try {
      this.loading = true
      const payload = cloneDeep(this.dataSearch)
      delete payload.count
      delete payload.skip

      // format dataSearch
      const searchConditionBlocks = formatSearchConditionBlocks(payload.searchConditionBlocks)
      const requestData = {
        condition: { ...payload, searchConditionBlocks },
        reportTemplateCode: code,
      }

      await this.$store.dispatch(EXPORT_BULK_REPORT_OUTPUT, requestData)

      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.BULK_REPORT_MO, isHaveNew: true })
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  get isDevelopmentEnv(): boolean {
    return this.$store?.getters?.isDevelopmentEnv
  }

  async downloadAttachedFile() {
    try {
      const payload = cloneDeep(this.dataSearch)
      delete payload.count
      delete payload.skip
      // format dataSearch
      const searchConditionBlocks = formatSearchConditionBlocks(payload.searchConditionBlocks)
      const dataSearch = { condition: { ...payload, searchConditionBlocks } } as IBarcodeSearch
      await exportBarCodeAttchFiles((dataSearch as unknown) as Record<string, unknown>)
      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.FILE, isHaveNew: true })
    } catch (error) {
      openMessage(error as string, 'error')
    }
  }

  async downloadCSV(headerType = '') {
    try {
      const payload = cloneDeep(this.dataSearch)
      delete payload.count
      delete payload.skip
      // format dataSearch
      const searchConditionBlocks = formatSearchConditionBlocks(payload.searchConditionBlocks)
      const dataSearch = { condition: { ...payload, searchConditionBlocks } } as Record<string, unknown>
      if (headerType !== '') {
        dataSearch.headerFormatCode = headerType
      }
      await exportBarCode((dataSearch as unknown) as Record<string, unknown>)
      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.BARCODE, isHaveNew: true })
    } catch (error) {
      openMessage(error as string, 'error')
    }
  }

  async downloadPDFBarcodes() {
    try {
      this.loading = true
      const condBlocks = [] as ISearchConditionBlock[]
      this.dataSearch.searchConditionBlocks.forEach((i) => condBlocks.push(cloneDeep(i)))
      const dataSearch = {
        condition: {
          ...this.dataSearch,
          count: EXPORT_BARCODES_LIMIT_NUMBER,
          searchConditionBlocks: formatSearchConditionBlocks(condBlocks),
        },
      } as IBarcodeSearch
      // const result = await getBarcodeList(dataSearch)
      const response = await getBarcodeList(dataSearch)
      this.exportBarcodes = response?.manufacturingPlans ?? []
      this.$refs.barcodePDFComponent.showComfirmPopup()
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  onEditBarcode(barcode: IManufacturingPlanSearch, _column: TableColumnCtx<IManufacturingPlanSearch>, event: MouseEvent) {
    const routeLocation = { name: 'barcodeView', params: { id: barcode.barcodeId } }
    if (event?.metaKey || event?.ctrlKey) {
      const resolved = this.$router.resolve(routeLocation)
      window.open(resolved.href, '_blank')
    } else {
      this.$router.push(routeLocation)
    }
  }

  // this option control - turn off onPageChange event trigger at first time when searching.
  isTriggerPageChanging = true
  async performSearch() {
    this.isTriggerPageChanging = false

    this.dataSearch.skip = 0
    await this.fetchBarcodesList(this.dataSearch, 'searchList')
  }

  async clearAllSearchForm() {
    const dataSearch = {
      ...initDataSearch(),
      projectCode: this.dataSearch.projectCode,
      version: this.dataSearch.version,
    }
    this.dataSearch = dataSearch
    this.searchFormKey++
    await this.fetchBarcodesList(dataSearch, this.listType)
  }

get savedSearchConditions(): ISavedSearchCondition[] {
  const all: ISavedSearchCondition[] =
    this.$store.state.profile?.user?.savedSearchConditions ?? []

  const filtered = all.filter((s) => {
    const c = s.conditionJson
    return (
      c?.projectCode === this.dataSearch.projectCode &&
      c?.version === this.dataSearch.version
    )
  })

  return orderBy(filtered, [], ['desc'])
}

  get matchedSavedCondition(): ISavedSearchCondition | null {
    if (!this.dataSearch?.searchConditionBlocks) return null

    const normalizeBlocks = (blocks: ISearchConditionBlock[]) =>
      formatSearchConditionBlocks(cloneDeep(blocks)).map((block) => ({
        ...block,
        logicalOperator: block.logicalOperator?.toLowerCase(),
        searchConditions: block.searchConditions.map((c) => {
          const cond = { ...c } as Record<string, unknown>
          delete cond.valueListOperator
          delete cond.valueDate
          return {
            ...cond,
            ...(typeof cond.valueType === 'string' && { valueType: cond.valueType.toLowerCase() }),
            ...(typeof cond.value === 'number' && { value: String(cond.value) }),
            ...(cond.minValue !== undefined && { minValue: String(cond.minValue) }),
            ...(cond.maxValue !== undefined && { maxValue: String(cond.maxValue) }),
          }
        }),
      }))

    const normalizedCurrent = normalizeBlocks(this.dataSearch.searchConditionBlocks)

    for (const saved of this.savedSearchConditions) {
      const rawCondition =
        typeof saved.conditionJson === 'string' ? JSON.parse(saved.conditionJson) : saved.conditionJson
      const condition = rawCondition as IBarcodeSearchForm
      if (!condition?.searchConditionBlocks) continue
      const savedBlocks = deserializeConditionBlocks(cloneDeep(condition.searchConditionBlocks))
      if (
        isEqual(normalizeBlocks(savedBlocks), normalizedCurrent) &&
        (condition.logicalOperator?.toLocaleLowerCase() ?? 'and') ===
          (this.dataSearch.logicalOperator?.toLocaleLowerCase() ?? 'and') &&
        (condition.isToday ?? true) === (this.dataSearch.isToday ?? true)
      ) {
        return saved
      }
    }
    return null
  }

  openSavedSearchDialog() {
    this.isSavedListVisible = true
  }

  async toggleSave() {
    if (this.matchedSavedCondition) {
      const matched = this.matchedSavedCondition
      this.$confirm(this.$t('confirm_delete_saved_search_condition'), this.$t('info'), {
        confirmButtonText: this.$t('delete'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('cancel'),
      })
        .then(async () => {
          try {
            await this.$store.dispatch(DELETE_SAVED_SEARCH_CONDITION, matched.id)
            openMessage(this.$t('delete_saved_search_condition_success'), 'success')
          } catch (error) {
            errorHandler(error)
          }
        })
        .catch(() => true)
    } else {
      this.isSaveDialogVisible = true
    }
  }

  async saveSearchCondition(name: string) {
    this.isSavingCondition = true
    try {
      const searchConditionBlocks = formatSearchConditionBlocks(cloneDeep(this.dataSearch.searchConditionBlocks))
      const condition = {
        ...this.dataSearch,
        searchConditionBlocks,
      }
      await this.$store.dispatch(CREATE_SAVED_SEARCH_CONDITION, {
        name,
        searchContext: this.dataSearch.projectCode ?? '',
        condition,
      })
      openMessage(this.$t('save_search_condition_success'), 'success')
      this.isSaveDialogVisible = false
    } catch (error) {
      errorHandler(error)
    } finally {
      this.isSavingCondition = false
    }
  }

  async applySavedSearch(condition: ISavedSearchCondition) {
    try {
      if (!condition.conditionJson) return
      const json = cloneDeep(condition.conditionJson)
      if (json.searchConditionBlocks) {
        json.searchConditionBlocks = deserializeConditionBlocks(json.searchConditionBlocks)
        const suppliers = this.$store.getters.getMasterDataSuppliers as Record<string, { code?: string }>
        const workcenters = this.$store.getters.getMasterDataWorkcenters as Record<string, { vendorCode?: string }>
        const warehouses = this.$store.getters.getMasterDataWarehouses as Record<string, { vendorCode?: string }>
        json.searchConditionBlocks.forEach((block) => {
          block.searchConditions.forEach((item) => {
            const isCombinedKey =
              item.key === BARCODE_SEARCH_KEYS.warehouseId || item.key === BARCODE_SEARCH_KEYS.workCenterId
            const hasSupplierExtras = item.warehouseId !== undefined || item.workCenterId !== undefined
            if (isCombinedKey && hasSupplierExtras) {
              item.key = BARCODE_SEARCH_KEYS.supplierId
              const vendorCode =
                (item.workCenterId ? workcenters?.[item.workCenterId]?.vendorCode : undefined) ??
                (item.warehouseId ? warehouses?.[item.warehouseId]?.vendorCode : undefined)
              if (vendorCode && suppliers) {
                const supplierKey = Object.keys(suppliers).find((k) => suppliers[k].code === vendorCode)
                if (supplierKey) item.value = supplierKey
              }
            }
          })
        })
      }
      this.isSavedListVisible = false
      this.dataSearch = json
      this.searchFormKey++
      this.currentPageNum = 1
      await this.fetchBarcodesList(this.dataSearch, 'searchList')
    } catch (e) {
      errorHandler(e)
    }
  }

  async created() {
    this.loadLatestImportHistory()
    if (!this.$store.state.project?.isLoaded) {
      this.loading = true
      await this.$store.dispatch(LOAD_PROJECTS_LIST)
      this.loading = false
    }

    const hasQueryData = !isEmpty(this.$route.query)
    if (hasQueryData && !this.isSetPresetSearch) {
      this.loadedURLSearchData()
      await this.fetchBarcodesList(this.dataSearch, 'searchList')
    } else {
      const isLoadedPreset = await this.loadClientPreset()
      if (isLoadedPreset) {
        await this.fetchBarcodesList(this.presetData, 'presetList')
      } else {
        this.dataSearch = initDataSearch()
        this.dataSearch.projectCode = this.getProjectsList?.[0].code ?? ''
        this.dataSearch.version = this.getProjectsList.find((i) => i.code === this.dataSearch.projectCode)?.version ?? 1
        await this.fetchBarcodesList(this.dataSearch, 'searchList')
      }
    }
  }

  loadedURLSearchData() {
    const queryData = this.$route.query
    this.dataSearch = initDataSearch()
    if (queryData.projectCode) {
      for (const key in queryData) {
        switch (key) {
          case 'sortOrder':
            this.dataSearch[key] = queryData[key] === TABLE_SORT_DESC ? -1 : queryData[key] === TABLE_SORT_ASC ? 1 : 0
            break
          case 'version':
            this.dataSearch[key] = Number(queryData[key])
            break
          case 'isToday':
            this.dataSearch[key] = queryData[key] === 'true'
            break
          case 'projectCode':
          case 'logicalOperator':
          case 'sortKey':
            this.dataSearch[key] = queryData[key]
            break
          case 'page':
            if (queryData.page) {
              this.currentPageNum = Number(queryData.page)
              const skipNum = (this.currentPageNum - 1) * Number(this.dataSearch.count)
              this.dataSearch.skip = skipNum
            } else {
              this.currentPageNum = 1
            }
            break
          default:
            this.dataSearch = this.getSearchConditionBlocks(this.dataSearch, queryData, key)
            break
        }
      }
    }
  }

  getSearchConditionBlocks(dataSearch: IBarcodeSearchForm, queryData: Record<string, string>, key: string) {
    const [mainKey, conditionIdx, blockIdx] = key.split('_')

    const idxNum = Number(conditionIdx)
    const blockIdxNum = Number(blockIdx)

    if (!dataSearch.searchConditionBlocks[blockIdxNum]) {
      dataSearch.searchConditionBlocks[blockIdxNum] = { searchConditions: [] }
    }
    const block = dataSearch.searchConditionBlocks[blockIdxNum]

    if (mainKey) {
      if (mainKey === 'logicalOperator') {
        block.logicalOperator = queryData[key]
      } else if (mainKey === 'valueList') {
        const valueList = queryData[key].split(',').map((item) => item.trim())
        block.searchConditions[idxNum].valueList = valueList as string[]
      } else {
        if (!block.searchConditions?.[idxNum]) block.searchConditions[idxNum] = {} as IBarcodeSearchFormItem
        if (mainKey === 'isExactValue') {
          block.searchConditions[idxNum][mainKey] = queryData[key].toLowerCase() === 'true'
        } else {
          block.searchConditions[idxNum][
            mainKey as
              | 'key'
              | 'subKey'
              | 'value'
              | 'minValue'
              | 'maxValue'
              | 'valueType'
              | 'bcTypeKey'
              | 'componentType'
          ] = queryData[key]
        }
      }
    }

    block.searchConditions.forEach((item: IBarcodeSearchFormItem) => {
      const isDate =
        item.valueType === BARCODE_SEARCH_VALUE_TYPES.date || item.valueType === BARCODE_SEARCH_VALUE_TYPES.dateTimeData
      const isDateTimeString = item.valueType === BARCODE_SEARCH_VALUE_TYPES.dateTimeString

      if (!isDate && !isDateTimeString) return

      const format = isDateTimeString ? TIMETABLE_DATETIME.DATE_TIME_FULL_FORMAT : undefined

      if (item.value) item.value = moment(item.value).format(format)
      if (item.minValue) item.minValue = moment(item.minValue).format(format)
      if (item.maxValue) item.maxValue = moment(item.maxValue).format(format)

      if (item.minValue && item.maxValue) {
        item.value = ([item.minValue, item.maxValue] as unknown) as string
      }
    })
    return dataSearch
  }

  redirectIndex(data: IBarcodeSearchForm, page: number) {
    this.$router.push({ name: this.routeName, query: buildSearchUrlQuery(data, page) })
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.header-container-wrapper {
  background-color: $body-bg;
}

.fixed-table:deep() {
  height: calc(100vh - 100px);
  min-width: 800px;

  &.table-content {
    overflow: auto;
    height: calc(100vh - 400px);
    min-height: 200px;
  }

  &.el-table {
    &__header-wrapper {
    }
    &__header {
      thead {
        tr:first-child {
          th:first-child {
            border-top-left-radius: 0px !important;
          }
          th:last-child {
            border-top-right-radius: 0px !important;
          }
        }
      }
    }
    &::before {
      height: 0px;
    }

    &__header-wrapper {
      position: sticky;
      position: -webkit-sticky;
      top: 0;
      z-index: 10;
    }
  }
}

.dialog-area :deep() {
  .el-overlay .el-dialog {
    max-width: 1000px;
  }

  .el-button--small {
    padding: 7px;
    min-height: 28px !important;
    margin-top: 3px;
  }
  .el-form-item {
    margin-bottom: 16px;
    &__item {
      top: calc(100% - 5px);
    }
  }
}

.header-bar {
  background-color: $light-gray;
}
.el-process-btn {
  &.orange-btn {
    &:hover {
      border-color: lightgoldenrodyellow;
      background-color: #fb923c;
      color: white;
    }
  }

  &.green-btn {
    background-color: #20cf90;
    border-color: #a5ebd2;
    color: white;
  }

  &.is-disabled {
    color: gray;
    background-color: $light-gray !important;
  }
}

.save-condition-icon {
  width: 25px;
  bottom: 48%;
  right: 2px;
  height: 25px;
  z-index: 100;
}

.pdf-download-btn {
  transition: all 0.3s ease;

  &:not(.is-disabled):hover {
    background-color: #f3f4f6;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.is-disabled {
    background-color: #f9fafb !important;
    border-color: #e5e7eb !important;
    cursor: not-allowed !important;
    opacity: 0.6;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
}
</style>
